import { Component, Host, h, Element, Prop } from '@stencil/core';
import { slotHasAnyContent, showSlotContent, showSlotParentContent } from '../../utils/slotHelpers';
import { colorRequiresBorder, decodeColor, mixColorForTheme } from '@aha-app/aha-shared';
import classcat from 'classcat';

export interface AhaCard extends HTMLAhaCardElement {}

/**
 * Starter component
 * @slot default - Contents of the default slot.
 */
@Component({
  tag: 'aha-card',
  styleUrl: 'aha-card.css',
  shadow: true,
})
export class AhaCard {
  @Element() host!: HTMLAhaCardElement;

  /**
   * Adds a selection outline to the card.
   */
  @Prop({ reflect: true }) selected: boolean = false;

  /**
   * Adds a shadow to the card.
   */
  @Prop({ reflect: true }) shadow: boolean = false;

  /**
   * Adds a highlight color to the card.
   */
  @Prop({ reflect: true, attribute: 'highlight-color' }) highlightColor?: string = undefined;

  /**
   * Uses the highlight-color prop and creates a theme based background color
   */
  @Prop({ reflect: true, attribute: 'use-background-color' }) useBackgroundColor?: boolean = false;

  componentDidRender() {
    const headerSlot = this.host.shadowRoot?.querySelector(
      'slot[name="header"]',
    ) as HTMLSlotElement;
    if (headerSlot) {
      slotHasAnyContent(headerSlot)
        ? headerSlot.removeAttribute('hidden')
        : headerSlot.setAttribute('hidden', '');
    }

    const imageSlot = this.host.shadowRoot?.querySelector('slot[name="image"]') as HTMLSlotElement;
    if (imageSlot && imageSlot.parentElement) {
      slotHasAnyContent(imageSlot)
        ? imageSlot.parentElement.removeAttribute('hidden')
        : imageSlot.parentElement.setAttribute('hidden', '');
    }
  }

  render() {
    const cardStyle = Object.create(null);
    if (this.highlightColor) {
      const decodedColor = decodeColor(this.highlightColor);
      const requiresBorder = colorRequiresBorder(decodedColor);
      cardStyle['--aha-card--border-left-color'] = decodedColor;
      if (requiresBorder) {
        cardStyle['--aha-card--border-left-right-border'] = '1px solid var(--theme-primary-border)';
      }
      if (this.useBackgroundColor) {
        cardStyle.backgroundColor = mixColorForTheme(decodedColor);
      }
    }

    return (
      <Host>
        <slot name="header" onSlotchange={showSlotContent}></slot>
        <div
          class={classcat({
            'aha-card__body': true,
            'with-highlight-color': this.highlightColor,
          })}
          style={cardStyle}
        >
          <div class="aha-card__image">
            <slot name="image" onSlotchange={showSlotParentContent}></slot>
          </div>
          <slot></slot>
        </div>
      </Host>
    );
  }
}
